'use es6';

import unescapedText from 'I18n/utils/unescapedText';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { SEARCH_RESULTS_MAX } from 'marketplace-ui-common/constants/search';
import { useMounted } from 'marketplace-ui-common/hooks/useMounted';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useScrollToTop } from 'marketplace-ui-common/hooks/useScrollToTop';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { isFulfilled, isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import ResultsMaxWarning from 'marketplace-ui-common/storefront/components/ResultsMaxWarning';
import { UsageScreenProvider } from 'marketplace-ui-tracking';
import { Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useLocation, useParams } from 'react-router-dom';
import { fetchProfiles } from '../actions/StorefrontActions';
import StorefrontDesktopFilters from '../components/StorefrontDesktopFilters';
import StorefrontPageMeta from '../components/StorefrontPageMeta';
import GenericError from '../components/error/GenericError';
import FilterPanelNavigator from '../components/filters/mobile/FilterPanelNavigator';
import NoResultsState from '../components/results/NoResultsState';
import { ResultsCallout } from '../components/results/ResultsCallout';
import ResultsFilters from '../components/results/ResultsFilters';
import { ResultsGrid } from '../components/results/ResultsGrid';
import ResultsHeader from '../components/results/ResultsHeader';
import UsageViewTracker from '../components/tracking/UsageViewTracker';
import * as FilterKey from '../constants/search/FilterKey';
import * as FilterKeys from '../constants/search/FilterKey';
import { DEFAULT_SEARCH_LIMIT } from '../constants/search/Filters';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import { getAccreditationsRequestState } from '../selectors/accreditations';
import { getCertificationsRequestState } from '../selectors/certifications';
import { getStorefrontProfiles, getStorefrontProfilesRequestState, getStorefrontProfilesTotal } from '../selectors/storefront';
import { getServices, getServicesRequestState } from '../slices/serviceCatalogSlice';
import { clearStorefrontFilters, getMultiSelectStorefrontFilters, updateStorefrontFilters, updateStorefrontFiltersFromLocation } from '../slices/storefrontFiltersSlice';
import { debug } from '../utils/env';
import { getHasFilters } from '../utils/filters';
import { getRecentActivity, setRecentActivity } from '../utils/homepage';
import { getStorefrontPageTitleText } from '../utils/i18n';
import { getOffsetFromPageNumber } from '../utils/pagination';
import { fromFilterStateToQuery } from '../utils/storefrontFilters';
import { getStorefrontFiltersEventProperties } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LoadingComponent = () => /*#__PURE__*/_jsx(LoadingSpinner, {});
const ProfilesStorefrontError = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "STOREFRONT_ERROR_LOAD",
  children: /*#__PURE__*/_jsx(GenericError, {})
});
const ProfilesStorefront = () => {
  const location = useLocation();
  const params = useParams();
  const debugEnabled = debug('storefront');
  const dispatch = useDispatch();
  const isMounted = useMounted();
  const prevMounted = usePrevious(isMounted);
  const {
    setDefaultEventProperties
  } = useContext(StorefrontUsageTrackerContext);
  const accreditationsRequestState = useSelector(getAccreditationsRequestState);
  const servicesRequestState = useSelector(getServicesRequestState);
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const profiles = useSelector(getStorefrontProfiles);
  const profilesTotal = useSelector(getStorefrontProfilesTotal);
  const profilesRequestState = useSelector(getStorefrontProfilesRequestState);
  const catalogServices = useSelector(getServices);
  const profilesRequestSucceeded = isSucceeded(profilesRequestState);
  const profilesRequestFulfilled = isFulfilled(profilesRequestState);
  const multiSelectFilters = useSelector(getMultiSelectStorefrontFilters);
  const prevFilters = usePrevious(multiSelectFilters);
  const {
    pathname
  } = location;
  const prevPathname = usePrevious(pathname);
  const {
    [FilterKey.SEARCH]: searchValue
  } = multiSelectFilters;
  const prevSearchValue = usePrevious(searchValue);
  const shouldTrackFilterView = useMemo(() => {
    return pathname === prevPathname && searchValue === prevSearchValue && multiSelectFilters !== prevFilters;
  }, [multiSelectFilters, pathname, prevFilters, prevPathname, prevSearchValue, searchValue]);
  const profilesCount = useMemo(() => {
    return Math.min(profilesTotal, SEARCH_RESULTS_MAX);
  }, [profilesTotal]);
  const pageCount = useMemo(() => {
    return Math.ceil(profilesCount / DEFAULT_SEARCH_LIMIT);
  }, [profilesCount]);
  const pageNumber = useMemo(() => {
    const {
      page
    } = params;
    return page ? parseInt(page, 10) : 1;
  }, [params]);
  const {
    maxVisiblePageButtons,
    paddingBlockEnd,
    paddingBlockStart,
    paddingInline,
    showFirstLastButtons
  } = useResponsiveProps({
    maxVisiblePageButtons: {
      xs: 0,
      sm: 5,
      md: undefined,
      lg: undefined,
      xl: undefined
    },
    showFirstLastButtons: {
      xs: false,
      sm: false,
      md: false,
      lg: false,
      xl: true
    },
    paddingBlockEnd: {
      xs: '500',
      md: '1000'
    },
    paddingBlockStart: {
      xs: '0',
      md: '1000'
    },
    paddingInline: {
      xs: '600',
      md: '1200'
    }
  });
  const getUsageEventProperties = useCallback((extras = {}) => {
    return Object.assign({}, getStorefrontFiltersEventProperties(multiSelectFilters, catalogServices), extras, {
      screen: 'storefront',
      pageNumber,
      totalResults: profilesCount
    });
  }, [catalogServices, multiSelectFilters, pageNumber, profilesCount]);
  useEffect(() => {
    setDefaultEventProperties(getUsageEventProperties());
  }, [getUsageEventProperties, setDefaultEventProperties]);
  const pageTitle = useMemo(() => {
    return getStorefrontPageTitleText(multiSelectFilters, catalogServices, profilesCount);
  }, [catalogServices, multiSelectFilters, profilesCount]);
  const handleClearFilters = useCallback(() => {
    dispatch(clearStorefrontFilters());
  }, [dispatch]);
  const handleDeleteFilter = useCallback((keys, values) => {
    keys.forEach((key, index) => {
      const updatedFilters = Object.assign({}, multiSelectFilters);
      if (typeof updatedFilters[key] === 'string') {
        updatedFilters[key] = '';
      } else {
        const removedValue = values[index];
        updatedFilters[key] = updatedFilters[key].filter(value => value !== removedValue);
      }
      dispatch(updateStorefrontFilters({
        updates: updatedFilters
      }));
    });
  }, [dispatch, multiSelectFilters]);
  const handleUpdateFilterKey = useCallback(newFilterObject => {
    dispatch(updateStorefrontFilters({
      updates: newFilterObject
    }));
  }, [dispatch]);
  const handlePaginatorOnPageChange = useCallback(({
    target: {
      value
    }
  }) => {
    dispatch(updateStorefrontFilters({
      updates: {
        [FilterKey.OFFSET]: getOffsetFromPageNumber(value, DEFAULT_SEARCH_LIMIT)
      }
    }));
  }, [dispatch]);
  const renderedResults = useMemo(() => {
    const showMaxWarning = pageNumber >= pageCount && profilesCount >= SEARCH_RESULTS_MAX;
    const hasResults = profilesRequestSucceeded && pageCount > 0 && profiles.size > 0;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(ResultsHeader, {
        filters: multiSelectFilters,
        onClearFilters: () => handleClearFilters(),
        onDeleteFilter: handleDeleteFilter,
        onUpdateFilter: handleUpdateFilterKey,
        requestState: profilesRequestState,
        hasResults: hasResults
      }), /*#__PURE__*/_jsxs(LoadingWrapper, {
        ErrorComponent: ProfilesStorefrontError,
        LoadingComponent: LoadingComponent,
        requestStatus: profilesRequestState,
        children: [/*#__PURE__*/_jsx("span", {
          role: "region",
          "aria-live": "assertive",
          "aria-label": unescapedText('ecosystem-marketplace-solutions-ui.storefront.results.header.accessibleSearchAnnouncement')
        }), hasResults ? /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(ResultsGrid, {
            results: profiles,
            searchValue: multiSelectFilters[FilterKeys.SEARCH],
            pageNumber: pageNumber
          }), /*#__PURE__*/_jsx(ResultsCallout, {}), showMaxWarning && /*#__PURE__*/_jsx(Flex, {
            marginBlockStart: "800",
            flexDirection: "row",
            justifyContent: "center",
            children: /*#__PURE__*/_jsx(ResultsMaxWarning, {})
          }), /*#__PURE__*/_jsx(Paginator, {
            maxVisiblePageButtons: maxVisiblePageButtons,
            page: pageNumber,
            pageCount: pageCount,
            onPageChange: handlePaginatorOnPageChange,
            showFirstLastButtons: showFirstLastButtons,
            paddingBlockStart: "800"
          })]
        }) : /*#__PURE__*/_jsx(NoResultsState, {
          searchTerm: multiSelectFilters[FilterKeys.SEARCH]
        })]
      })]
    });
  }, [handleClearFilters, handleDeleteFilter, handlePaginatorOnPageChange, handleUpdateFilterKey, maxVisiblePageButtons, multiSelectFilters, pageCount, pageNumber, profiles, profilesCount, profilesRequestState, profilesRequestSucceeded, showFirstLastButtons]);
  useScrollToTop(location);
  useEffect(() => {
    if (isMounted) {
      const recentActivity = getRecentActivity();
      if (getHasFilters(multiSelectFilters)) {
        const hasSearch = multiSelectFilters.search && multiSelectFilters.search.length;
        let recentActivityType = 'filter';
        if (hasSearch) {
          recentActivityType = 'search';
        } else if (location.search.includes('ref=questionnaire')) {
          recentActivityType = 'questionnaire';
        }
        setRecentActivity(Object.assign({}, recentActivity, {
          type: recentActivityType,
          filters: multiSelectFilters,
          viewedProfiles: recentActivityType === 'search' || recentActivityType === 'filter' ? [] //remove viewed profile history when search/filters are updated
          : recentActivity && recentActivity.viewedProfiles
        }));
      }
    }
  }, [dispatch, isMounted, location.search, multiSelectFilters]);
  useEffect(() => {
    const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(multiSelectFilters);
    if (isMounted && filtersChanged) {
      // Only refetch profiles if filters have been changed
      dispatch(fetchProfiles(fromFilterStateToQuery(multiSelectFilters)));
    } else if (isMounted && !prevMounted) {
      // Initial profiles fetch after mounting
      dispatch(fetchProfiles(fromFilterStateToQuery(multiSelectFilters)));
    }
  }, [dispatch, isMounted, multiSelectFilters, prevFilters, prevMounted]);
  useEffect(() => {
    dispatch(updateStorefrontFiltersFromLocation({
      location,
      params
    }));
  }, [catalogServices, dispatch, location, params]);
  useEffect(() => {
    if (multiSelectFilters[FilterKey.OFFSET] && !profiles.size) {
      dispatch(updateStorefrontFilters({
        updates: {
          [FilterKey.OFFSET]: 0
        }
      }));
    }
  }, [dispatch, multiSelectFilters, profiles]);
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);
  useEffect(() => {
    if (debugEnabled) {
      console.groupCollapsed('[Storefront] debugging');
      console.log('location:\n', location);
      console.log('filters:\n', multiSelectFilters);
      console.groupEnd();
    }
  }, [debugEnabled, location, multiSelectFilters]);
  return /*#__PURE__*/_jsxs(UsageScreenProvider, {
    screen: "storefront",
    children: [profilesRequestSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: "STOREFRONT_LOAD"
    }), /*#__PURE__*/_jsx(StorefrontPageMeta, {
      accreditationsRequestState: accreditationsRequestState,
      certificationsRequestState: certificationsRequestState,
      servicesRequestState: servicesRequestState,
      filters: multiSelectFilters,
      location: location,
      pageCount: pageCount,
      pageNumber: pageNumber,
      pageTitle: pageTitle,
      profilesRequestState: profilesRequestState,
      profilesTotal: profiles.size
    }), /*#__PURE__*/_jsx(MediaQueryView, {
      max: ScreenSizes.SM,
      children: /*#__PURE__*/_jsx(ResultsFilters, {
        marginBlockStart: "400",
        paddingInlineEnd: "400",
        startSlot: /*#__PURE__*/_jsx(FilterPanelNavigator, {
          filters: multiSelectFilters,
          onClearFilters: () => handleClearFilters(),
          onDeleteFilter: handleDeleteFilter,
          onSetFilter: handleUpdateFilterKey
        }),
        filters: multiSelectFilters,
        onClearFilters: () => handleClearFilters(),
        onCloseFilterTag: handleDeleteFilter
      })
    }), /*#__PURE__*/_jsx(MainSection, {
      paddingBlockStart: paddingBlockStart,
      paddingBlockEnd: paddingBlockEnd,
      paddingInline: paddingInline,
      "data-test-id": "storefront-results-section",
      color: "light",
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(MediaQueryView, {
            min: ScreenSizes.SM,
            children: /*#__PURE__*/_jsxs(Grid, {
              children: [/*#__PURE__*/_jsx(GridItem, {
                size: {
                  xs: 12,
                  md: 3
                },
                children: /*#__PURE__*/_jsx(StorefrontDesktopFilters, {
                  filters: multiSelectFilters
                })
              }), /*#__PURE__*/_jsx(GridItem, {
                size: {
                  xs: 12,
                  md: 9
                },
                children: renderedResults
              })]
            })
          }), /*#__PURE__*/_jsx(MediaQueryView, {
            max: ScreenSizes.SM,
            children: renderedResults
          })]
        })
      })
    }), /*#__PURE__*/_jsx(UsageViewTracker, {
      active: profilesRequestFulfilled,
      name: "storefront-pageview",
      properties: getUsageEventProperties
    }, `${pathname}${searchValue ? `-${searchValue}` : ''}`), shouldTrackFilterView && /*#__PURE__*/_jsx(UsageViewTracker, {
      active: profilesRequestFulfilled,
      name: "storefront-filter-view",
      properties: getUsageEventProperties
    })]
  });
};
export default ProfilesStorefront;